import React, { useContext, useEffect } from 'react';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { setDocumentUploadStatus } from '../personal-info/document-upload-slice';
import { DomesticPartnerWizard } from './wizard/domestic-partner-wizard';
import { AnnualEnrollmentContext } from '../../../annual-enrollment/models/annual-enrollment-models';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { DomesticPartnerForm } from './domestic-partner-form';
import { getCurrentDomesticPartner, getCurrentSpouse } from '../relationships/relationships-selectors';
import { DependentsState } from '../dependents/dependents-model';
import { useDependents } from '../../hooks/useDependents';
import { useQuery } from '../../../../global/hooks/useQuery';
import { PersonSearchContext, PersonSearchKeys } from '../../../arc/features/person-search/person-search-model';
import { OSCRRService } from '../../../arc/features/person-search/oscrr-helpers';
import { BaseApiState } from '../../../../global/models/api-model';
import { FormTypes } from '../../models/form-models';
import { useSpouseInformation } from '../../hooks/useSpouseInfo';
import { RelationshipsState } from '../relationships/relationships-model';
import { AccountInfoRoutes } from '../../constants/routes';

interface Props {
	basePath?: string;
	customWizard?: DomesticPartnerWizard;
}
function DomesticPartnerApp(props: Props) {
	const { basePath, customWizard } = props;
	const apiState = useSelector<TcsmStates, BaseApiState>(state => state.apiState);
	const app = useContext(DemographicsContext);
	const context = useContext(AnnualEnrollmentContext);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
	const dependentsLoaded = useDependents(personGuid);
	const spousesLoaded = useSpouseInformation(personGuid);
	const relationshipState = useSelector<TcsmStates, RelationshipsState>(state => state.relationships[app]);
	const dependentsState = useSelector<TcsmStates, DependentsState>(state => state?.dependents[app]);
	const currentDomesticPartner = getCurrentDomesticPartner(dependentsState?.dependents?.domesticPartners);
	const currentSpouse = getCurrentSpouse(relationshipState.spouses);

	const action = useQuery()?.get('action');
	const history = useHistory();
	const dispatch = useDispatch();
	const wizard = customWizard ?? new DomesticPartnerWizard(epiProps, context, app, dispatch, history, currentDomesticPartner, action);
	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(setDocumentUploadStatus(false, app));
	}, []);

	if (!dependentsLoaded || !spousesLoaded) {
		return <LoadingCircle />;
	}

	if (currentSpouse) {
		return <Redirect to={`${basePath}${AccountInfoRoutes.RelationshipsSegment}?warning=${FormTypes.HAVE_SPOUSE_WARNING}`} />;
	}

	return (
		<PersonSearchContext.Provider value={PersonSearchKeys.DomesticPartner}>
			{(OSCRRService.IsOSCRRWorking(apiState) ? <LoadingCircle /> : null)}
			<DomesticPartnerForm wizard={wizard} basePath={basePath} />
			{(OSCRRService.IsOSCRRWorking(apiState) ? <LoadingCircle /> : null)}
		</PersonSearchContext.Provider>
	);
}

DomesticPartnerApp.defaultProps = {
	basePath: '',
	customWizard: null,
};


export default DomesticPartnerApp;